import { FormGroupDirective, NgForm, UntypedFormControl } from '@angular/forms';
import { GMT_IST_DIFFERENCE_SEC, INDEXSYMBOL, Utils } from './_helpers';
import { FilterOptions, QUOTE_UPDATE_TYPE, QuoteStoreService, SEARCH_SYMBOL_INSTRUMENT_TYPE, SingleIndexQuotes, SocketDepth, SymbolSearchService } from './_shared';
import { CASH_TO_OPTIONS_EXCHANGE, EXCHANGE_ID_VALUES } from './_shared/modules/portfolio-form/portfolio-form.enums';
import { MarketIDMap } from './_shared/shared.enums';
import { MarginSummaryList, MarginSummaryResponse, MarketWatchTable, OrderDepthinterface, OrderDepthResponseInterface, SymbolRowRequest } from './app.interface';
import { MarketKeywords } from './app.structs';
import { MarketWatchQuote } from './modules/trading/modules/market-watch/structs/market-watch.interface';

export class ErrorMatcher {
    isErrorState(control: UntypedFormControl | null, form: FormGroupDirective | NgForm | null): boolean {
        const invalidCtrl = (control && control.invalid && control.parent.touched);
        const invalidParent = (control && control.parent && (control.parent.hasError('compErr') ||
            control.parent.hasError('notSame') ||
            control.parent.hasError('sameNewOld')));
        return (invalidCtrl || invalidParent) && control.touched;
    }
}

export class TPErrorMatcher {
    isErrorState(control: UntypedFormControl | null, form: FormGroupDirective | NgForm | null): boolean {
        const invalidCtrl = (control && control.invalid && control.parent.touched);
        const invalidParent = (control && control.parent && (control.parent.hasError('TargetProfitError')
            || control.parent.hasError('TargetProfitRequired')));
        return (invalidCtrl || invalidParent) && control.touched;
    }
}
export class SLErrorMatcher {
    isErrorState(control: UntypedFormControl | null, form: FormGroupDirective | NgForm | null): boolean {
        const invalidCtrl = (control && control.invalid && control.parent.touched);
        const invalidParent = (control && control.parent && (control.parent.hasError('StopLossError')
            || control.parent.hasError('stopLossRequired')));
        return (invalidCtrl || invalidParent) && control.touched;
    }
}

export class TrailByErrorMatcher {
    isErrorState(control: UntypedFormControl | null, form: FormGroupDirective | NgForm | null): boolean {
        const invalidCtrl = (control && control.invalid && control.parent.touched);
        const invalidParent = (control && control.parent && (control.parent.hasError('TrailByError')));
        return (invalidCtrl || invalidParent) && control.touched;
    }
}

export class LockProfitErrorMatcher {
    isErrorState(control: UntypedFormControl | null, form: FormGroupDirective | NgForm | null): boolean {
        const invalidCtrl = (control && control.invalid && control.parent.touched);
        const invalidParent = (control && control.parent && (control.parent.hasError('LockProfitError')));
        return (invalidCtrl || invalidParent) && control.touched;
    }
}

export class TimeRangeErrorMatcher {
    isErrorState(control: UntypedFormControl | null, form: FormGroupDirective | NgForm | null): boolean {
        const invalidCtrl = (control && control.invalid && control.touched);
        const invalidParent = (control && control.parent && (control.parent.hasError('TimeError')
            || control.parent.hasError('exitTimeError')
            || control.parent.hasError('DateError') || control.parent.hasError('StartDateError')
            || control.parent.hasError('EndDateError')
            || control.parent.hasError('CurrentTimeError')
            || control.parent.hasError('RangeMinError')))
            || control.parent.hasError('RangeMaxError');
        return (invalidCtrl || invalidParent);
    }
}

export class ControlErrorMatcher {
    isErrorState(control: UntypedFormControl | null, form: FormGroupDirective | NgForm | null): boolean {
        return !!(control && control.invalid && (control.dirty || control.touched));
    }
}

/**
* Throttler To add throttle over quote feed we collect feed in these throttle classes from quotestore
* time : to check whether the curtime is greater or equal to lastupdated quote time + our throttle time
*/

export class Throttler {
    time: number;
    _quoteStore: QuoteStoreService;
    constructor(time: number = 0, quoteStore: QuoteStoreService) {
        this.time = time;
        this._quoteStore = quoteStore;
    }

}



/**
* Normal Throttler workes different as we update through symbolId
* we store all the incoming quotes from feed which are pending into pendingQuoteUpdate
* quotesToUpdate: to store which quotes are to update
* lastUpdateMap: to store last updated time of that quote
* quoteType: as NormalThrottler can be used for many types such as for quotes or index
*/

export class NormalThrottler extends Throttler {
    pendingQuoteUpdate: Set<string> = new Set<string>();
    subscribedSymbols = new Set<string>();
    quotesToUpdate = new Set<string>();
    // Map to Store market_id,security_id -> LastUpdatedTime
    lastUpdateMap = new Map<string, number>();
    quoteType: QUOTE_UPDATE_TYPE;


    constructor(time: number = 0, quoteStore: QuoteStoreService, quoteType: QUOTE_UPDATE_TYPE) {
        super(time, quoteStore);
        this.quoteType = quoteType;
        this.listenFeed();
    }

    listenFeed() {
        this._quoteStore.getFeedSubject(this.quoteType).subscribe(symbolId => {
            if (this.subscribedSymbols.has(symbolId)) {
                if (!this.pendingQuoteUpdate.has(symbolId)) {
                    this.pendingQuoteUpdate.add(symbolId);
                }
            }
        });

    }

}

export class MarginSummary implements MarginSummaryList {
    margin_percent = 0;
    mtm_percent = 0;
    mtm_level = 0;
    cash = 0;
    client_code = '';
    pay_in = 0;
    pay_out = 0;
    adhoc = 0;
    credit_for_sale = 0;
    option_sell_premium = 0;
    margin_utilized = 0;
    mtm_profit = 0;
    booked_profit = 0;
    cash_available = 0;
    mtm_loss = 0;
    booked_loss = 0;
    option_buy_premium = 0;
    collateral = 0;
    net_mtm = 0;
    net_booked_profit = 0;
    precision = 0;
    custom_component = 0;
    osp_credited = 0;
    delivery_sell_benefit = 0;
    deductions = 0;
    exposure = 0;
    delivery_margin = 0;
    span = 0;
    var = 0;
    elm = 0;
    withdrawable_margin = 0;

    constructor(marginSummaryResponse: MarginSummaryResponse) {
        Object.keys(this).forEach(key => {
            this[key] = marginSummaryResponse[key];
        });

        if (!this.precision) {
            this.precision = 2;
        }
    }
}

export class MarketWatch implements MarketWatchTable {
    /**
     * Definition of Properties is important
     * as Interfaces dont exist on Runtime.
     */
    security_id = 0;
    symbol = '';
    symbol_id = 0;
    scrip_name = '';
    market_id = '';
    scripName = '';
    instrument_type = '';
    series = '';
    oi_qty = null;
    oi_value = null;
    oi_change = null;
    marketName = '';
    maturity_date = null;
    bid_qty = null;
    bid_price = null;
    ask_price = null;
    ask_qty = null;
    last_trade_price = null;
    last_trade_qty = null;
    last_trade_time = null;
    last_update_time = null;
    change_in_price = null;
    open_price = null;
    high_price = null;
    low_price = null;
    close_price = null;
    hi_52_wk = null;
    lo_52_wk = null;
    perChange = null;
    upper_ckt_limit = null;
    lower_ckt_limit = null;
    volume = null;
    value = null;
    trend = null;
    total_bid_qty = null;
    total_ask_qty = null;
    expiry = '';
    expiry_date = '';
    strike_price = null;
    option_type = '';
    isin = '';
    avg_trade_price = null;
    scrip_precision = 2;
    qty_precision = 0;
    maturity_yearmon = '';
    maturity_yearmon2 = '';
    contract_type = '';
    underlying_id = null;
    suspended = '';
    tick_size = 0;
    symbolId = null;
    market_lot = 1;
    isTickUp = null;
    isTickDown = null;
    is_index = false;
    expiry_date2 = '';
    trade_value = 1;
    asm_gsm_surveillance_indicator = 0;
    fo_banned_status = false;
    options_exists = true;
    indicative_close_price = null;

    constructor(marketWatchResponse?: any, skipTimeAdjustment?: boolean) {
   
        if (marketWatchResponse) {
            if (!skipTimeAdjustment) {
                // Last trade time is coming +5.30 hours extra from mcl. So, same formula is used in web and exe.
                if (marketWatchResponse.last_trade_time) {
                    marketWatchResponse.last_trade_time -= GMT_IST_DIFFERENCE_SEC;
                }

                if (marketWatchResponse.last_update_time) {
                    marketWatchResponse.last_update_time -= GMT_IST_DIFFERENCE_SEC;
                }
            }

            if (marketWatchResponse.market_id === MarketKeywords.MCX) {
                marketWatchResponse.isin = null;
            }



            Object.keys(marketWatchResponse).forEach(key => {
                this[key] = marketWatchResponse[key];
            });

        }
    }

    checkOptionsExists(symbolSearchService: SymbolSearchService) {
        try {
            if (this.is_index) {
                this.options_exists = INDEXSYMBOL[this.symbol?.toUpperCase()] ? true : false;
            } else if (this.instrument_type !== SEARCH_SYMBOL_INSTRUMENT_TYPE.OPTION) {
                const symbol = this.symbol.substring(0, this.symbol.lastIndexOf(' ') !== -1 ? this.symbol.lastIndexOf(' ') : this.symbol.length);
                const symbols = symbolSearchService.advanceSearch(`${CASH_TO_OPTIONS_EXCHANGE[parseInt(this.market_id)]} ${symbol}`,
                    SEARCH_SYMBOL_INSTRUMENT_TYPE.OPTION, FilterOptions.symbol, null);

                if (!symbols?.length) {
                    this.options_exists = false;
                }
            }
        } catch (e) {
            console.error(e);
        }
    }

    setQuotesData(quote: any) {
        const marketId = MarketIDMap[quote.market_id];
        const securityId = quote.security_id;
        if (quote && (marketId === this.market_id && securityId === this.security_id)) {
            // if (this.is_index) {
            //     quote = Utils.indexToSymbolQuote(quote as SingleIndexQuotes);
            // }
            if ((quote as MarketWatchQuote).last_trade_price &&
                ((quote as MarketWatchQuote).last_trade_price > (quote as MarketWatchQuote).close_price)) {
                this.isTickUp = !(this.isTickDown = false);
            } else if ((quote as MarketWatchQuote).last_trade_price &&
                ((quote as MarketWatchQuote).last_trade_price < (quote as MarketWatchQuote).close_price)) {
                this.isTickDown = !(this.isTickUp = false);
            }

            Object.keys(quote).forEach(key => {
                this[key] = quote[key];
                if (key === 'market_id') {
                    this.market_id = EXCHANGE_ID_VALUES[quote[key]];
                }
                // Last trade time and Last Updated Time is coming +5.30 hours extra from mcl. So, same formula is used in web and exe.
                if (key && (key === 'last_trade_time' || key === 'last_update_time')) {
                    this[key] -= GMT_IST_DIFFERENCE_SEC;
                }
            });


            this.hi_52_wk = (quote['high_price'] && this.hi_52_wk) && ( Utils.applyPrecision(quote['high_price'], this.scrip_precision) >= this.hi_52_wk) ? Utils.applyPrecision(quote['high_price'], this.scrip_precision) : this.hi_52_wk;
            this.lo_52_wk = (quote['low_price'] && this.lo_52_wk) && ( Utils.applyPrecision(quote['low_price'], this.scrip_precision) <= this.lo_52_wk) ? Utils.applyPrecision(quote['low_price'], this.scrip_precision) : this.lo_52_wk;
            if (this.last_trade_price && this.close_price) {
                this.change_in_price = this.last_trade_price - this.close_price;
                this.perChange = ((this.change_in_price / this.close_price) * 100);
            }
        }
    }

    copyKeysFromObject(obj: any) {
        Object.keys(obj).forEach(key => {
            this[key] = obj[key];
        });
    }
}

export class SockDepth {
    depth = [];

    constructor(depth: SocketDepth) {
        const arr = [];
        for (let i = 0; i < depth.buy.length; i++) {
            {
                arr.push(this.CreateObj([
                    depth.sell[i].price,
                    depth.buy[i].price,
                    depth.sell[i].qty,
                    depth.buy[i].qty,
                    depth.sell[i].no_of_order,
                    depth.buy[i].no_of_order
                ]));
            }
        }

        this.depth = arr;
    }

    CreateObj(depthArr) {
        return {
            ask: depthArr[0],
            bid: depthArr[1],
            askMktVol: depthArr[2],
            bidMktVol: depthArr[3],
            numberOfAsks: depthArr[4],
            numberOfBids: depthArr[5]
        };
    }
}

export class SymbolRow implements SymbolRowRequest {
    symbol_id = 0;
    security_id = 0;
    market_id = '';
    is_index = false;
    scrip_precision = 2;
    constructor(symbolRowRequest?: SymbolRowRequest) {
        if (symbolRowRequest) {
            Object.keys(this).forEach(key => {
                this[key] = symbolRowRequest[key];
            });
            if (!symbolRowRequest.is_index) {
                this.is_index = false;
            }
        }
    }
}


export class OrderDepth {

    depth: OrderDepthinterface[];
    scripPrecision = 2;
    data: OrderDepthResponseInterface;

    constructor(orderDepthReponse: OrderDepthResponseInterface, marketData: MarketWatch) {
        if (marketData.scrip_precision) {
            this.scripPrecision = marketData.scrip_precision;
        }
        if (orderDepthReponse && orderDepthReponse.buy && orderDepthReponse.sell) {
            this.depth = this.cleanDepth(orderDepthReponse);
        }
    }

    cleanDepth(symbolDepth: OrderDepthResponseInterface) {
        let depth: OrderDepthinterface[] = [];
        if (symbolDepth) {
            for (let i = 0; i < symbolDepth.buy.length; i++) {
                {
                    depth.push({
                        ask: symbolDepth.sell[i].price,
                        bid: symbolDepth.buy[i].price,
                        askMktVol: symbolDepth.sell[i].qty,
                        bidMktVol: symbolDepth.buy[i].qty,
                        numberOfAsks: symbolDepth.sell[i].no_of_order,
                        numberOfBids: symbolDepth.buy[i].no_of_order
                    });
                }
            }
        } else {
            depth = Utils.generateFakeDepth();
        }
        return depth;
    }
}

export class UpdatedThrottler extends Throttler {
    pendingQuoteUpdate: Set<string> = new Set<string>();
    subscribedSymbols = new Set<string>();
    quotesToUpdate = new Set<string>();
    // Map to Store SymbolId -> LastUpdatedTime
    lastUpdateMap = new Map<string, number>();
    quoteType: QUOTE_UPDATE_TYPE;


    constructor(time: number = 0, quoteStore: QuoteStoreService, quoteType: QUOTE_UPDATE_TYPE) {
        super(time, quoteStore);
        this.quoteType = quoteType;
        this.listenFeed();
    }

    listenFeed() {
        this._quoteStore.getFeedSubject(this.quoteType).subscribe(key => {
            if (this.subscribedSymbols.has(key)) {
                if (!this.pendingQuoteUpdate.has(key)) {
                    this.pendingQuoteUpdate.add(key);
                }
            }
        });

    }

}